import { makeStyles } from '@material-ui/styles'
import {
  Typography as MuiTypography,
  Paper as MuiPaper,
  Grid as MuiGrid,
  styled,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'
import clsx from 'clsx'
import BrandCard from '../BrandCard'
import { Button, Icon, NavigationList } from '@/components'
import { remoteConfig } from '../../../firebase'
import { useNavigation } from '@/app/hooks'
import { BrandType } from '@/app/components/BrandNotFound/BrandNotFound'
import { ThermostatBrand, VehicleBrand } from '@/types/brand'
import VehicleSearch from '@/app/pages/connect-device/search/vehicle-search'
import { useState } from 'react'
import { Link } from '@/app/components/Link'
import { useIsAvaBasePath } from '@/app/hooks/ava/useAvaVerification'
import { useUrlSearchParam } from '@/hooks'
import Card from '@/components/Card/Card'

const Typography = styled(MuiTypography)(spacing)
const Grid = styled(MuiGrid)(spacing)
const Paper = styled(MuiPaper)(spacing)

const useStyles = makeStyles(() => ({
  selectionArea: {
    borderRadius: 20,
    backgroundColor: '#FFFFFF',
    padding: '5px 20px 5px 20px',
    maxHeight: 500,
    overflowY: 'auto',
    width: '100%',
  },
  insetBottom: {
    boxShadow: 'inset 0 -20px 20px -20px rgb(0 0 0 / 20%)',
  },
  mainTitle: {
    fontSize: 20,
    fontWeight: 700,
    textAlign: 'left',
    marginBottom: 30,
  },
}))

const BrandSelector = ({
  brandList,
  onButtonSelect,
  id,
  titleStyle = '',
  selectorAreaStyle = '',
  includeNames = true,
  title = 'Select Your Brand',
  includeInsetBottom = true,
  handleGoBack,
  brandType,
  suggestCharger: suggestChargerParam,
}: {
  brandList: VehicleBrand[] | ThermostatBrand[]
  onButtonSelect: (brand: VehicleBrand | ThermostatBrand) => void
  id: string
  titleStyle?: string
  selectorAreaStyle?: string
  includeNames?: boolean
  title?: string
  includeInsetBottom?: boolean
  handleGoBack?: () => void
  brandType: BrandType
  suggestCharger?: boolean
}) => {
  // if the suggestCharger param is not provided, default to true
  const suggestCharger =
    suggestChargerParam !== undefined ? suggestChargerParam : true
  const navigation = useNavigation()
  const classes = useStyles()
  const [brandListFiltered, setBrandListFiltered] = useState(brandList)
  function handleSearch(value: string) {
    value = value.toLowerCase()
    setBrandListFiltered(
      brandList.filter((brand) => brand.name.toLowerCase().includes(value)),
    )
  }

  const isAva = useIsAvaBasePath()
  const connectChargerLink = isAva
    ? '/ava/connect-device/charger/select'
    : '/connect-device/charger/select'
  const avaProgramId = remoteConfig?.getWhitelabelId('ava') ?? null
  const isOnboarding = useUrlSearchParam('onboarding')

  return (
    <Grid container>
      <Grid container item>
        {handleGoBack && (
          <Grid item>
            <Button
              id={`${id}-navigation-page-back`}
              variant="icon"
              className="mr-5 !p-0"
              onClick={handleGoBack}
            >
              <Icon name="ArrowLeft" size={24} color="grey-900" />
            </Button>
          </Grid>
        )}
        <Grid item>
          <Typography
            variant="h4"
            className={clsx(classes.mainTitle, titleStyle)}
          >
            {title}
          </Typography>
        </Grid>
      </Grid>
      {brandType === 'vehicle' && (
        <div className={'w-full mb-4'}>
          <VehicleSearch onChange={handleSearch} />
        </div>
      )}
      <Card className="w-full">
        {brandListFiltered.map((make, index, array) => (
          <BrandCard
            make={make}
            id={id}
            backSoon={make.backSoon}
            ineligible={make.ineligible}
            onClick={onButtonSelect}
            index={index}
            arrayLength={array.length}
            key={make.name}
            includeName={includeNames}
            includeLastBorder={true}
          />
        ))}
        <NavigationList>
          <NavigationList.BasicItem
            key="i-dont-see-my-brand"
            id="i-dont-see-my-brand-nav-item"
            variant="character"
            character="?"
            label="I don't see my brand"
            onClick={() => navigation.push(`/connect-${brandType}/not-found`)}
            labelClasses="!text-base self-start flex self-center"
            characterClasses="self-center flex self-start m-auto"
          />
        </NavigationList>
      </Card>
      {brandType === 'vehicle' && suggestCharger && (
        <div className="flex justify-center w-full my-4">
          <Link to={connectChargerLink}>Connect my charger instead</Link>
        </div>
      )}
      {isAva && avaProgramId && isOnboarding && (
        <div className="flex justify-center w-full my-4">
          <Link to={`/ava/program-enrollment/${avaProgramId}/offer`}>
            Skip and enroll my address
          </Link>
        </div>
      )}
    </Grid>
  )
}

export default BrandSelector
